import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { employeeService, studentService, deviceService } from '@/services/api'
import Modal from '@/components/common/Modal'
import { Monitor, CheckSquare, Square, Send, Loader2 } from 'lucide-react'
import toast from 'react-hot-toast'

export default function SyncToTerminalModal({ open, onClose, personType, personIds, allSelected = false }) {
  const [selectedIds, setSelectedIds] = useState([])
  const [syncing, setSyncing] = useState(false)
  const [force, setForce] = useState(false)

  const { data: devicesData, isLoading } = useQuery({
    queryKey: ['devices-for-sync'],
    queryFn: () => deviceService.list({ is_active: true }).then((r) => r.data.results || r.data),
    enabled: open,
  })

  const devices = devicesData || []
  const allDevicesSelected = devices.length > 0 && selectedIds.length === devices.length

  const toggleDevice = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    )
  }

  const toggleAll = () => {
    setSelectedIds(allDevicesSelected ? [] : devices.map((d) => d.id))
  }

  const handleClose = () => {
    setSelectedIds([])
    onClose()
  }

  const handleSync = async () => {
    if (selectedIds.length === 0) {
      toast.error('Выберите хотя бы один терминал')
      return
    }
    setSyncing(true)
    try {
      let resp
      const hasSpecificIds = Array.isArray(personIds) && personIds.length > 0

      if (hasSpecificIds && !allSelected) {
        const service = personType === 'student' ? studentService : employeeService
        resp = await service.bulkRegisterDevice({
          ids: personIds,
          device_ids: selectedIds,
          force,
        })
      } else {
        // "Select all" или кнопка без выбора — синхронизируем всех активных,
        // но обязательно фильтруем по типу (students / employees) чтобы не лить всё подряд
        resp = await deviceService.bulkSync(selectedIds, force, personType || null)
      }
      toast.success(resp.data.message || 'Синхронизация запущена')
      handleClose()
    } catch (err) {
      toast.error(err.response?.data?.error || 'Ошибка синхронизации')
    } finally {
      setSyncing(false)
    }
  }

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title="Отправить на терминал"
      size="sm"
      footer={
        <>
          <button className="btn-secondary" onClick={handleClose} disabled={syncing}>
            Отмена
          </button>
          <button
            className="btn-primary flex items-center gap-2"
            onClick={handleSync}
            disabled={syncing || selectedIds.length === 0}
          >
            {syncing ? (
              <Loader2 size={16} className="animate-spin" />
            ) : (
              <Send size={16} />
            )}
            {syncing
              ? 'Отправка...'
              : `Отправить на ${selectedIds.length || ''} терминал${selectedIds.length > 1 ? 'а' : ''}`}
          </button>
        </>
      }
    >
      <div className="space-y-3">
        <p className="text-sm text-gray-500">
          {(() => {
            const typeLabel = personType === 'student' ? 'студенты' : personType === 'employee' ? 'сотрудники' : 'пользователи'
            if (allSelected) {
              return `Все активные ${typeLabel} будут загружены на выбранные терминалы.`
            }
            if (personIds && personIds.length > 0) {
              return `Выбранные ${typeLabel} (${personIds.length}) будут загружены на выбранные терминалы.`
            }
            return personType
              ? `Все активные ${typeLabel} будут загружены на выбранные терминалы.`
              : 'Все активные пользователи (студенты и сотрудники) будут загружены на выбранные терминалы.'
          })()}
        </p>

        {isLoading ? (
          <div className="flex justify-center py-6">
            <Loader2 size={24} className="animate-spin text-gray-400" />
          </div>
        ) : devices.length === 0 ? (
          <div className="text-center py-6 text-gray-400 text-sm">
            Нет активных устройств
          </div>
        ) : (
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <button
                onClick={toggleAll}
                className="flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-700"
              >
                {allDevicesSelected ? <CheckSquare size={15} /> : <Square size={15} />}
                {allDevicesSelected ? 'Снять все' : 'Выбрать все'}
              </button>

              <label className="flex items-center gap-2 cursor-pointer group">
                <input
                  type="checkbox"
                  checked={force}
                  onChange={(e) => setForce(e.target.checked)}
                  className="checkbox checkbox-sm"
                />
                <span className="text-xs text-gray-500 group-hover:text-gray-700">Перезаписать (Force)</span>
              </label>
            </div>

            <div className="border border-gray-200 rounded-xl divide-y divide-gray-100 max-h-64 overflow-y-auto">
              {devices.map((device) => (
                <label
                  key={device.id}
                  className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={selectedIds.includes(device.id)}
                    onChange={() => toggleDevice(device.id)}
                    className="rounded text-primary"
                  />
                  <Monitor
                    size={16}
                    className={
                      device.status === 'online' ? 'text-green-500' : 'text-gray-400'
                    }
                  />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-gray-900 truncate">
                      {device.name}
                    </div>
                    <div className="text-xs text-gray-400 truncate">
                      {device.ip_address}
                      {device.location ? ` — ${device.location}` : ''}
                    </div>
                  </div>
                  <span
                    className={`text-xs px-2 py-0.5 rounded-full font-medium ${device.status === 'online'
                      ? 'bg-green-100 text-green-700'
                      : 'bg-gray-100 text-gray-500'
                      }`}
                  >
                    {device.status === 'online' ? 'Онлайн' : 'Офлайн'}
                  </span>
                </label>
              ))}
            </div>
          </div>
        )}
      </div>
    </Modal>
  )
}
